import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, IForecastPayload, Targetable, Targeting } from '@feathr/blackbox';
import { toast } from '@feathr/components';
import {
  determineInterestsMatchMode,
  getAgeCategoryIds,
  getGeoFilterIds,
  getGeoTargetingsArray,
  getInterestIds,
  getNonArchivedTargetings,
} from '@feathr/extender/components/Targetings';
import { getInterestChannels } from '@feathr/extender/components/Targetings/Interests/Interests.utils';
import useCampaignChannelVisibility from '@feathr/extender/hooks/useCampaignChannelVisibility';
import { useStore } from '@feathr/extender/state';
import { useDebouncedCallback } from '@feathr/hooks';
import type { ListResponse } from '@feathr/rachis';

import {
  buildChannelsArray,
  parseChannelForecast,
} from './AdWizardAudienceStep.EstimatedReach.utils';

interface IUseEstimatedReachProps {
  /** The list of targetings associated with the campaign. */
  targetings: ListResponse<Targeting>;
  /** The campaign to fetch reach estimates for. */
  campaign: Campaign;
}

interface IUseEstimatedReach {
  /** Whether a forecast fetch is currently in progress. */
  isLoading: boolean;
  /** Estimated reach for Display Ads, or null if unavailable. */
  displayReach: number | null;
  /** Estimated reach for Meta Ads, or null if unavailable. */
  metaReach: number | null;
  /** Whether the Display Ads channel is enabled for this campaign. */
  showDisplay: boolean;
  /** Whether the Meta Ads channel is enabled for this campaign. */
  showMeta: boolean;
}

/**
 * Hook that fetches per-channel audience reach estimates for the Audience step.
 *
 * Derives channel visibility internally via `useCampaignChannelVisibility` and
 * uses the channel-aware forecast endpoint to return both `displayReach` and
 * `metaReach`.
 *
 * @param props - Hook configuration including campaign and targetings.
 * @param props.targetings - The list of targetings for the campaign.
 * @param props.campaign - The campaign to fetch reach estimates for.
 * @returns Object with `isLoading`, `displayReach`, `metaReach`, `showDisplay`, and `showMeta`.
 */
function useEstimatedReach({
  targetings,
  campaign,
}: IUseEstimatedReachProps): Readonly<IUseEstimatedReach> {
  const { t } = useTranslation();
  const { showDisplay, showMeta } = useCampaignChannelVisibility(campaign);
  const { Segments, AgeDemographicTargetables, InterestTargetables, Targetables } = useStore();
  const [isLoading, setIsLoading] = useState(false);
  const [displayReach, setDisplayReach] = useState<number | null>(null);
  const [metaReach, setMetaReach] = useState<number | null>(null);
  const { isOmniReachCampaign } = campaign;
  const abortControllerRef = useRef<AbortController | null>(null);
  const interestsCacheRef = useRef<Targetable[] | null>(null);

  const shouldFetchEstimatedReach = isOmniReachCampaign && !targetings.isPending;

  async function buildForecastPayload(
    targetings: ListResponse<Targeting>,
  ): Promise<IForecastPayload> {
    const targetingsArray = targetings.models;

    const { included: includedGeoFilterIds, excluded: excludedGeoFilterIds } =
      await getGeoFilterIds(targetingsArray, Targetables);

    if (!interestsCacheRef.current) {
      // Integrations do not change mid-session so the cache stays valid.
      const channel = getInterestChannels(campaign.get('integrations'));
      const { targetables } = await InterestTargetables.getInterests(channel);
      interestsCacheRef.current = targetables;
    }

    const ageCategoryIds = await getAgeCategoryIds(targetingsArray, Targetables);
    const { interestIds, matchMode } = getInterestIds(targetingsArray, interestsCacheRef.current);

    // Add to an array to be spread and to avoid passing an empty array of interest_ids
    const interestsPayload =
      matchMode === 'match_any'
        ? [{ interest_ids: interestIds }]
        : interestIds.map((id) => ({ interest_ids: [id] }));

    // Add to an array to be spread and to avoid passing an empty array of age_category_ids
    const ageCategoryPayload = [{ age_category_ids: ageCategoryIds }];

    const allAgeDemographicsIds = AgeDemographicTargetables.list()
      .models.map((model) => model.get('age_category'))
      .filter((ageCategory): ageCategory is string => typeof ageCategory === 'string');

    const hasOnlyLocationTargetings =
      interestIds.length === 0 &&
      ageCategoryIds.length === 0 &&
      (includedGeoFilterIds.length > 0 || excludedGeoFilterIds.length > 0);

    const locationOnlyAgeDemographics = hasOnlyLocationTargetings
      ? [{ age_category_ids: allAgeDemographicsIds }]
      : [];

    return {
      ...(includedGeoFilterIds.length > 0 && { included_geo_filter_ids: includedGeoFilterIds }),
      ...(excludedGeoFilterIds.length > 0 && { excluded_geo_filter_ids: excludedGeoFilterIds }),
      datagroup_data: [
        ...(interestIds.length > 0 ? interestsPayload : []),
        // If only locations have been passed, force all available age demographics because the
        // forecast endpoint requires some data ids
        ...(ageCategoryIds.length > 0 ? ageCategoryPayload : locationOnlyAgeDemographics),
      ],
    };
  }

  // We added or removed a targeting, refetch the estimated reach
  const numTargetings = getNonArchivedTargetings(targetings.models).length;
  // We changed the interests match mode, refetch the estimated reach
  const interestsMatchMode = determineInterestsMatchMode(targetings.models);
  // We changed a geo targeting location or inclusion/exclusion, refetch the estimated reach
  const geoTargetings = JSON.stringify(getGeoTargetingsArray(targetings.models));

  const fetchEstimatedReach = useCallback(async (): Promise<void> => {
    if (targetings.isPending) {
      setIsLoading(false);
      return;
    }

    // Abort any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create a new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const channels = buildChannelsArray(showDisplay, showMeta);

      // Skip the request when no channels are enabled — the channel-aware
      // endpoint requires at least one channel, and there's nothing to forecast.
      if (channels.length === 0) {
        if (!abortController.signal.aborted) {
          setDisplayReach(null);
          setMetaReach(null);
        }
        return;
      }

      const payload = await buildForecastPayload(targetings);

      const response = await Segments.getChannelForecast(
        { ...payload, channels },
        abortController.signal,
      );

      const parsed = parseChannelForecast(response);

      // Only update state if this request wasn't aborted
      if (!abortController.signal.aborted) {
        setDisplayReach(parsed.displayReach);
        setMetaReach(parsed.metaReach);
      }
    } catch (error) {
      // Don't show error toast if the request was aborted
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }

      // Only update state if this request wasn't aborted
      if (!abortController.signal.aborted) {
        toast(
          t("We couldn't fetch estimated reach. Try again later.", {
            error,
          }),
          {
            type: 'error',
          },
        );
        setDisplayReach(null);
        setMetaReach(null);
      }
    } finally {
      // Only update loading state if this request wasn't aborted
      if (!abortController.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [targetings, Segments, Targetables, t, showDisplay, showMeta]);

  // Debounce the fetch function - it will wait 1000ms after the last change
  const debouncedFetchEstimatedReach = useDebouncedCallback(fetchEstimatedReach, 1000, [
    numTargetings,
    interestsMatchMode,
    geoTargetings,
    targetings.isPending,
    showDisplay,
    showMeta,
  ]);

  // Set loading immediately when dependencies change (before debounce)
  useEffect(() => {
    if (shouldFetchEstimatedReach) {
      setIsLoading(true);
    }
  }, [
    shouldFetchEstimatedReach,
    numTargetings,
    interestsMatchMode,
    geoTargetings,
    targetings.isPending,
    showDisplay,
    showMeta,
  ]);

  // Fetch the estimated reach when dependencies change (debounced)
  useEffect(() => {
    if (shouldFetchEstimatedReach) {
      debouncedFetchEstimatedReach();
    }

    // Cleanup: abort any in-flight request when effect is cleaned up
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [
    shouldFetchEstimatedReach,
    numTargetings,
    interestsMatchMode,
    geoTargetings,
    targetings.isPending,
    showDisplay,
    showMeta,
    debouncedFetchEstimatedReach,
  ]);

  return {
    isLoading: isLoading || targetings.isPending,
    displayReach,
    metaReach,
    showDisplay,
    showMeta,
  };
}

export type { IUseEstimatedReach, IUseEstimatedReachProps };
export { useEstimatedReach };
